/**
 * License Request Guard — frontend API-level safeguard.
 *
 * Intercepts outgoing mutating requests (POST/PUT/DELETE/PATCH) and blocks
 * them if the org's license doesn't cover the target section/feature.
 *
 * This is an ADDITIONAL layer on top of:
 *   - Route-level gating (LicenseGatedRoute)
 *   - Component-level gating (useLicenseGate hook)
 *   - Backend decorator gating (@require_section, @require_ai_feature)
 *
 * It provides defense-in-depth: even if a UI button isn't properly gated,
 * the API call won't fire.
 */
import { AxiosRequestConfig } from "axios";
import store from "../store";
import { accessAiFeatures, accessSections } from "../store/actions/PlanActions";

const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === "true";

/* ─────────────────────────────────────────────────────────
   URLs that are ALWAYS allowed (infra, auth, settings, etc.)
   Checked first for fast-path exit.
   ───────────────────────────────────────────────────────── */
const ALWAYS_ALLOWED: RegExp[] = [
  /^\/auth/,
  /^\/licensing/,
  /^\/user-profile/,
  /^\/user/,
  /^\/settings/,
  /^\/notifications/,
  /^\/health/,
  /^\/feedback/,
  /^\/support/,
  /^\/payments/,
  /^\/onboarding/,
  /^\/menu/,
  /^\/dropdowns/,
  /^\/roles/,
  /^\/permissions/,
  /^\/webpubsub/,
  /^\/admin/,
  /^\/system/,
  /^\/errors/,
  /^\/devices/,
  /^\/recycle/,
  /^\/audit/,
  /^\/organization/,
  /^\/external/,
];

/* ─────────────────────────────────────────────────────────
   AI feature gates — checked BEFORE section gates because
   they're more specific (an AI endpoint within a section).
   ───────────────────────────────────────────────────────── */
const API_AI_FEATURE_MAP: Array<{ pattern: RegExp; feature: string }> = [
  // Drafts AI
  { pattern: /^\/drafts\/generate/,                       feature: "drafts" },
  { pattern: /^\/draft\/.*\/custom_instruction/,          feature: "drafts" },
  { pattern: /^\/matter-draft\/.*\/custom_instruction/,   feature: "drafts" },
  // Insights AI
  { pattern: /^\/insights\/generate/,                     feature: "insights" },
  { pattern: /^\/insights\/[^/]+\/[^/]+$/,                feature: "insights" },
  { pattern: /^\/matter-insight\/.*\/custom_instruction/,  feature: "insights" },
  // Tables AI processing
  { pattern: /^\/tables\/[^/]+\/process/,                 feature: "tables_ai" },
  { pattern: /^\/tables\/[^/]+\/process-column/,          feature: "tables_ai" },
  { pattern: /^\/tables\/[^/]+\/process-rows/,            feature: "tables_ai" },
  // Template AI
  { pattern: /^\/templates\/structures\/create$/,         feature: "template_ai" },
  { pattern: /^\/templates\/structure\/[^/]+\/reanalyze/, feature: "template_ai" },
  { pattern: /^\/templates\/branding\/extract/,           feature: "template_ai" },
  // Review AI
  { pattern: /^\/reviews\/[^/]+\/start/,                  feature: "review_ai" },
  { pattern: /^\/reviews\/[^/]+\/re-review/,              feature: "review_ai" },
  // Data source indexing
  { pattern: /^\/datasources\/create/,                    feature: "data_source_indexing" },
  { pattern: /^\/datasources\/[^/]+\/index/,              feature: "data_source_indexing" },
  // Client onboarding AI
  /* Also not gated, and for the same reason: pipeline is not sold, so the
     client pre-check would refuse a call for a feature nobody has bought
     access to yet. The server still decides. */
];

/* ─────────────────────────────────────────────────────────
   Section gates — maps API URL prefix to license section.
   Only mutating requests (POST/PUT/DELETE/PATCH) are checked.
   GETs always pass through for read-only access.
   ───────────────────────────────────────────────────────── */
const API_SECTION_MAP: Array<{ pattern: RegExp; section: string }> = [
  // Matters
  { pattern: /^\/matters/,                section: "matters" },
  { pattern: /^\/cases/,                  section: "matters" },
  // Contacts (formerly Clients)
  { pattern: /^\/clients/,               section: "contacts" },
  { pattern: /^\/contacts/,              section: "contacts" },
  // Tasks
  { pattern: /^\/tasks/,                 section: "tasks" },
  // Files / Documents
  { pattern: /^\/files/,                 section: "files" },
  { pattern: /^\/documents/,             section: "files" },
  // Research
  { pattern: /^\/research/,              section: "research" },
  // Tables
  { pattern: /^\/tables/,               section: "tables" },
  // Analytics
  { pattern: /^\/analytics/,            section: "analytics" },
  // Data Sources / Knowledge Base
  { pattern: /^\/datasources/,          section: "data_sources" },
  { pattern: /^\/data-sources/,         section: "data_sources" },
  // Templates
  { pattern: /^\/templates/,            section: "templates" },
  /* Drafting is a TOP-LEVEL page (AB#1573) with a section of its own, so its
     calls gate on `drafting` rather than borrowing `matters`. Insights rides
     the same section: `/drafting/insights/*` is one product and one blueprint
     with `/drafting/drafts/*` (backend efb8f20). */
  { pattern: /^\/drafting/,             section: "drafting" },
  { pattern: /^\/drafts/,               section: "drafting" },
  { pattern: /^\/draft\//,              section: "matters" },
  { pattern: /^\/matter-draft/,         section: "matters" },
  { pattern: /^\/engagement_letter/,    section: "matters" },
  // Insights
  { pattern: /^\/insights/,             section: "matters" },
  { pattern: /^\/matter-insight/,       section: "matters" },
  /* Client Pipeline is NOT gated. It is a separate feature from Projects and
     is not sold yet, so nothing here should refuse its calls. I previously
     mapped it to `projects` because the backend aliases the retired
     `client_pipeline` grant key there — but that alias is about a dead key,
     not about what the page is. An unmapped path is an open one. */
  /* Budgets, time entries and matter pricing — the €19 module, NOT `finance`,
     which Starter already carries for invoices and payments. */
  { pattern: /^\/matter-economics/,     section: "matter_economics" },
  { pattern: /^\/economics/,            section: "matter_economics" },
  // Finance / Billing
  { pattern: /^\/finance/,              section: "finance" },
  { pattern: /^\/billing/,              section: "finance" },
  // People / Teams / Departments
  { pattern: /^\/org-people\/teams/,          section: "teams" },
  { pattern: /^\/org-people\/departments/,    section: "departments" },
  { pattern: /^\/org-people/,                 section: "people" },
  // Review / Playbook
  { pattern: /^\/reviews/,              section: "reviews" },
  { pattern: /^\/playbooks/,            section: "reviews" },
  // Calendar
  { pattern: /^\/calendar/,             section: "calendar" },
  // Messages / Chat
  { pattern: /^\/chat/,                 section: "messages" },
  { pattern: /^\/messages/,             section: "messages" },
];

/* ─────────────────────────────────────────────────────────
   Guard function — called from the axios request interceptor.
   Returns { allowed: true } or { allowed: false, code, ... }.
   ───────────────────────────────────────────────────────── */
export function checkLicenseBeforeRequest(
  config: AxiosRequestConfig
): { allowed: true } | { allowed: false; code: string; section?: string; feature?: string } {
  if (!LICENSING_ENABLED) return { allowed: true };

  // Only gate mutating methods
  const method = (config.method || "get").toUpperCase();
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
    return { allowed: true };
  }

  const url = config.url || "";
  // Interceptor receives relative URLs (e.g. "/matters/create")
  const path = url.startsWith("http") ? new URL(url).pathname : url;

  // Fast-path: always-allowed endpoints
  for (const re of ALWAYS_ALLOWED) {
    if (re.test(path)) return { allowed: true };
  }

  // Read license state from Redux store (synchronous — no React needed)
  const state = store.getState();
  const access = state.license?.access;
  const loaded = state.license?.loaded;

  /* Don't block if the licence data has not loaded — or if what loaded is not
     believable. An empty section list means every plan grants nothing, which
     no plan does; see `isUsableAccess`. */
  if (!loaded || !access) return { allowed: true };
  if (accessSections(access).length === 0) return { allowed: true };

  // Hard floor: a user without a base seat cannot mutate ANY licensed
  // resource. The ALWAYS_ALLOWED list above covers the carve-outs they
  // still need (auth, settings, /licensing/feature-request, payments,
  // notifications, admin/account pages, etc.). Anything else falls through
  // to the NoSeatGate placeholder on the frontend.
  const hasBaseSeat = access.has_base_seat ?? true;
  if (!hasBaseSeat) {
    return { allowed: false, code: "NO_BASE_SEAT" };
  }

  const licensedSections = accessSections(access);
  const licensedAiFeatures = accessAiFeatures(access);

  // 1) Check AI feature gates first (more specific than section gates)
  for (const { pattern, feature } of API_AI_FEATURE_MAP) {
    if (pattern.test(path)) {
      if (!licensedAiFeatures.includes(feature)) {
        return { allowed: false, code: "AI_FEATURE_NOT_LICENSED", feature };
      }
      // AI feature is licensed — still need to check the section below
      break;
    }
  }

  // 2) Check section gates
  for (const { pattern, section } of API_SECTION_MAP) {
    if (pattern.test(path)) {
      if (!licensedSections.includes(section)) {
        return { allowed: false, code: "SECTION_NOT_LICENSED", section };
      }
      return { allowed: true };
    }
  }

  // No matching rule — unmapped endpoints are not restricted
  return { allowed: true };
}
